import {Injectable} from '@angular/core';
import {AsyncSubject, Observable, Subject, Subscriber} from "rxjs";
import {ApiClientService} from "../api-client/api-client.service";
import {ApiResponse} from "../../api/api-response";
import {StorageService} from "../storage/storage.service";
import {NavigationStart, Router} from "@angular/router";
import {Config} from "../../models/config";
import {Partner} from '../../models/partner';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private bearer: string = '';
  private bearerExpireAt: null | Date = null;
  private config: Config | null = null;
  private asyncConfig: AsyncSubject<Config> = new AsyncSubject<Config>();

  public loginEvent: Subject<boolean> = new Subject<boolean>();

  constructor(
    private apiClient: ApiClientService,
    private storage: StorageService,
    private router: Router,
  ) {
    if (this.isStoredSession()) {
      const expireAt = new Date(this.storage.get('gizmo_expireAt'));
      const now = new Date();

      if (expireAt > now) {
        this.setBearer(this.storage.get('gizmo_token'), this.storage.get('gizmo_expireAt'));
      }
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadConfig();
      }
    });
  }

  isStoredSession(): boolean {
    return this.storage.get('gizmo_token') && this.storage.get('gizmo_expireAt');
  }

  isLogged(): boolean {
    if (this.bearer.length === 0) {
      return false;
    }

    const now = new Date();

    return !(!this.bearerExpireAt || now > this.bearerExpireAt);
  }

  getBearer(): string {
    return this.bearer;
  }

  setBearer(bearer: string, expireAt: Date, rememberMe: boolean = false): void {
    this.bearer = bearer;
    this.bearerExpireAt = expireAt;

    this.storage.set('gizmo_token', bearer, rememberMe ? 7 : null);
    this.storage.set('gizmo_expireAt', expireAt, rememberMe ? 7 : null);
  }

  login(username: string, password: string, rememberMe: boolean): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>): void => {
      this.apiClient.post('/login', null, {
        username,
        password
      }).subscribe({
        next: (response: ApiResponse): void => {
          this.setBearer(
            response.getData().bearer,
            response.getData().expireAt,
            rememberMe
          );

          subscriber.next();
          this.loginEvent.next(true);
        },
        error: (error): void => {
          if (error.code === 401) {
            subscriber.error('Incorrect username or password.');
          } else {
            subscriber.error(error.message);
          }
        }
      });
    });
  }

  logout(): void {
    this.bearer = '';
    this.bearerExpireAt = null;
    this.storage.remove('gizmo_token');
    this.storage.remove('gizmo_expireAt');

    this.loginEvent.next(false);

    this.router.navigateByUrl('/login').then();
  }

  loadConfig(forceReload: boolean = false): void {
    if (!this.config && this.isLogged() || forceReload) {
      this.apiClient.get('/config').subscribe((response: ApiResponse) => {
        this.config = new Config().deserialize(response.getData());
        this.asyncConfig.next(this.config);
        this.asyncConfig.complete();
      });
    }
  }

  getConnectedUser(): Observable<Partner | null> {
    return new Observable<Partner | null>((subscriber) => {
      this.asyncConfig.subscribe((config) => {
        if (config?.partner) {
          subscriber.next(config?.partner ?? null);
          subscriber.complete();
        }
      });
    });
  }

  getConfig(): Config | null {
    return this.config;
  }

  getAsyncConfig(): Observable<Config | null> {
    return new Observable<Config | null>(subscriber => {
      this.asyncConfig.subscribe((config) => {
        subscriber.next(config);
        subscriber.complete();
      });
    });
  }

  hasRoles(roles: string[]): Observable<boolean> {
    return new Observable<boolean>((subscriber: Subscriber<boolean>) => {
      this.asyncConfig.subscribe((config) => {
        for (const role of roles) {
          if (!config?.roles?.includes(role)) {
            subscriber.next(false);
            return;
          }
        }

        subscriber.next(true);
      });
    });
  }

  reloadConnectedUser(): Observable<Partner | null> {
    return new Observable<Partner | null>((subscriber: Subscriber<Partner | null>) => {
      this.apiClient.get('/partner').subscribe((apiResponse: ApiResponse) => {
        if (this.config) {
          this.config.partner = new Partner().deserialize(apiResponse.getData());
        }

        subscriber.next(this.config?.partner ?? null);
        subscriber.complete();
      })
    });
  }
}
